import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone } from 'lucide-react';
import SEO from '../components/SEO';
import CrisisSupport from '../components/CrisisSupport';
import ScrollIndicator from '../components/ScrollIndicator';
import './AssessmentPage.css';

const Referrers = () => {
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) entry.target.classList.add('revealed');
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });
    const els = document.querySelectorAll('.scroll-reveal');
    els.forEach((el) => observer.observe(el));
    return () => els.forEach((el) => observer.unobserve(el));
  }, []);

  return (
    <div className="page-assessment" data-testid="referrers-page">
      <SEO
        title="Information for Referrers | GPs, Paediatricians & NDIS | Clinical Minds"
        description="Information for GPs, paediatricians and NDIS providers referring to Clinical Minds Psychology. Autism, ADHD, FASD and learning assessments in Coffs Harbour. Referral process and available services."
        canonicalUrl="https://www.clinicalminds.net/referrers"
      />
      <ScrollIndicator />

      <section id="main-content" className="interior-hero" data-testid="page-hero">
        <div className="container">
          <span className="section-label">Referrers</span>
          <h1 className="heading-hero">Information for Referrers</h1>
          <p className="hero-subline">For GPs, paediatricians and NDIS providers</p>
        </div>
      </section>

      <section className="assess-section scroll-reveal">
        <div className="container assess-narrow">
          <div className="assess-body">
            <p className="body-medium">
              Clinical Minds Psychology provides comprehensive neurodevelopmental assessments and therapy in Coffs Harbour and via Telehealth. I assess young people and adults Australia-wide, including via regular visiting clinics, and I work closely with referring professionals to ensure coordinated care. All assessments are conducted personally by Lachlan from intake through to report writing; no components are outsourced.
            </p>
            <p className="body-medium" style={{ marginTop: '1rem' }}>
              Lachlan's peer-reviewed research includes published work on substance use and on the needs of caregivers of people experiencing first episode psychosis. See <Link to="/about#publications" className="assess-link">Research and Publications</Link>.
            </p>
          </div>
        </div>
      </section>

      <section className="assess-section assess-alt scroll-reveal">
        <div className="container assess-narrow">
          <span className="section-label">Our Services</span>
          <h2 className="heading-1">Services we offer</h2>
          <div className="assess-body">
            <ul>
              <li><Link to="/autism-assessment" className="assess-link">Autism assessments</Link> - recognised tools including ADOS-2, MIGDAS, DASI</li>
              <li><Link to="/adhd-assessment" className="assess-link">ADHD assessments</Link> - aligned with Australian Clinical Guidelines</li>
              <li><Link to="/fasd-assessment" className="assess-link">FASD assessments</Link> - comprehensive neurodevelopmental evaluations</li>
              <li><Link to="/learning-assessment" className="assess-link">Learning assessments</Link> - specific learning disorders, cognitive assessment</li>
              <li><Link to="/therapy" className="assess-link">Individual therapy</Link> - anxiety, depression, trauma, adjustment</li>
              <li>NDIS assessments and capacity-building reports</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="assess-section scroll-reveal">
        <div className="container assess-narrow">
          <span className="section-label">The Process</span>
          <h2 className="heading-1">How to refer</h2>
          <div className="assess-body">
            <p className="body-medium">
              Referrals can be submitted online, or patients and families can contact us directly. A Mental Health Treatment Plan is required for Medicare-rebated services.
            </p>
            <p className="body-medium" style={{ marginTop: '1rem' }}>
              <Link to="/refer" className="assess-link">Submit a referral online →</Link>
            </p>
          </div>
        </div>
      </section>

      <section className="assess-section assess-alt scroll-reveal">
        <div className="container assess-narrow">
          <span className="section-label">What Happens Next</span>
          <h2 className="heading-1">What to expect</h2>
          <div className="assess-body">
            <p className="body-medium">
              We'll confirm current availability when you refer. I value close communication with referring professionals and provide detailed reports that support ongoing treatment planning.
            </p>
            <p className="body-medium" style={{ marginTop: '1rem' }}>
              <Link to="/what-to-expect" className="assess-link">View the full assessment journey →</Link>
            </p>
          </div>
        </div>
      </section>

      <section className="assess-cta-section" data-testid="referrers-cta">
        <div className="container">
          <div className="assess-cta-card">
            <h2 className="heading-2">Questions about referring?</h2>
            <p className="body-large">We're happy to discuss whether our services are a good fit.</p>
            <div className="assess-cta-buttons">
              <Link to="/contact" className="btn-primary" data-testid="referrers-enquiry-btn">
                Get in Touch
              </Link>
              <a href="tel:0266002710" className="assess-phone-cta">
                <Phone size={18} /> 02 6600 2710
              </a>
            </div>
          </div>
        </div>
      </section>

      <CrisisSupport />
    </div>
  );
};

export default Referrers;
